// src/hooks/useVendingData.ts
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { getVendingHistory } from "../services/selfService";

interface VendingFilters {
  startDate: string;
  endDate: string;
  paymentMethod?: number;
  transactionStatus?: number;
  meterNumber?: string;
}

interface FilterState {
  paymentMethod: number | null;
  transactionStatus: number | null;
  searchText: string;
  startDate: string;
  endDate: string;
}

const DEFAULT_FILTERS: FilterState = {
  paymentMethod: null,
  transactionStatus: null,
  searchText: "",
  startDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
  endDate: moment().format("YYYY-MM-DD"),
};

interface UseVendingDataProps {
  autoFetch?: boolean;
  pageSize?: number;
  enableFilters?: boolean; // New prop to control filters
}

export const useVendingData = ({ 
  autoFetch = true, 
  pageSize = 10,
  enableFilters = true 
}: UseVendingDataProps = {}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filter state - only used when enableFilters is true
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [pendingFilters, setPendingFilters] = useState<FilterState>(DEFAULT_FILTERS);
  
  // Check if any filters are applied
  const hasActiveFilters = useCallback(() => {
    if (!enableFilters) return false;
    
    return (
      filters.paymentMethod !== null ||
      filters.transactionStatus !== null ||
      filters.searchText !== "" ||
      filters.startDate !== DEFAULT_FILTERS.startDate ||
      filters.endDate !== DEFAULT_FILTERS.endDate
    );
  }, [filters, enableFilters]);

  // Build API payload
  const buildPayload = useCallback((page: number = currentPage) => {
    const from = page
    
    const parameter: VendingFilters = {
      startDate: moment(filters.startDate, 'YYYY-MM-DD').startOf('day').toISOString(),
      endDate: moment(filters.endDate, 'YYYY-MM-DD').endOf('day').toISOString(),
    };

    // Only include optional filters if enableFilters is true and they have values
    if (enableFilters) {
      if (filters.paymentMethod !== null && filters.paymentMethod >= 0) {
        parameter.paymentMethod = filters.paymentMethod;
      }
      
      if (filters.transactionStatus !== null && filters.transactionStatus >= 0) {
        parameter.transactionStatus = filters.transactionStatus;
      }
      
      if (filters.searchText) {
        parameter.meterNumber = filters.searchText;
      }
    }

    return {
      pageSize,
      from,
      parameter,
    };
  }, [filters, currentPage, pageSize, enableFilters]);

  // Fetch data
  const fetchData = useCallback(async (page: number = 1) => {
    setLoading(true);
    setError(null);

    try {
      const payload = buildPayload(page);
      const response = await getVendingHistory(payload);

      if (response?.data?.isSuccessful && response?.data?.data) {
        setData(response.data.data.vendingHistory);
        setTotalCount(response.data.totalCount);
        setCurrentPage(page);
      } else {
        setError(response.data.message);
        toast.error(response.data.message);
      }
    } catch (err: any) {
      const errorMessage = err.data?.message || err.message || "Failed to fetch transactions";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [buildPayload]);

  // Update filters (for desktop - immediate fetch)
  const updateFilters = useCallback((newFilters: Partial<FilterState>) => {
    if (!enableFilters) return;
    
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, [enableFilters]);

  // Update pending filters (for mobile - wait for apply)
  const updatePendingFilters = useCallback((newFilters: Partial<FilterState>) => {
    if (!enableFilters) return;
    
    setPendingFilters((prev) => ({ ...prev, ...newFilters }));
  }, [enableFilters]);

  // Apply filters (for mobile)
  const applyFilters = useCallback(() => {
    if (!enableFilters) return;
    
    setFilters(pendingFilters);
    setCurrentPage(1);
  }, [pendingFilters, enableFilters]);

  // Reset filters
  const resetFilters = useCallback(() => {
    if (!enableFilters) return;
    
    setFilters(DEFAULT_FILTERS);
    setPendingFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  }, [enableFilters]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Fetch data when filters or page changes
  useEffect(() => {
    if (autoFetch) {
      fetchData(currentPage);
    }
  }, [filters, currentPage, autoFetch, fetchData]);

  return {
    // Data
    data,
    loading,
    error,
    totalCount,
    currentPage,
    pageSize,
    
    // Filters (only relevant when enableFilters is true)
    filters,
    pendingFilters,
    hasActiveFilters: hasActiveFilters(),
    
    // Actions
    fetchData,
    updateFilters,
    updatePendingFilters,
    applyFilters,
    resetFilters,
    handlePageChange,
    refetch: () => fetchData(currentPage),
  };
};

 
